import { ButtonList, Collapsible, Icon, Text } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { Fragment, useCallback, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useClickAway } from 'react-use';

import { useBreakpointValue } from 'utils/mediaQuery';
import { useServerStorage } from 'utils/useServerStorage';

import { Item } from './components/menuItem';
import { useSettingsMenu } from './hooks/useSettingsMenu';
import styles from './menu.module.scss';
import { MenuCategoryId, menuCategoryIds } from './menuCategories';
import { MenuItem as MenuItemT } from './types';

const defaultCollapsedState = menuCategoryIds.reduce((acc, title) => {
  acc[title] = false;
  return acc;
}, {} as Record<MenuCategoryId, boolean>);

export function Menu(): JSX.Element {
  const menu = useSettingsMenu();
  const { pathname } = useLocation();
  const { data, set } = useServerStorage('user');

  const {
    settings_category_collapsed: settingsCollapsedState = defaultCollapsedState,
  } = data;

  const firstAccessibleItem = menu[0]?.items[0];
  const currentLocationMenuItem = menu
    .reduce((menu, { items }) => [...menu, ...items], [] as MenuItemT[])
    .find(({ route }) => pathname.includes(route));

  const [activeItem, setActiveItem] = useState<MenuItemT>(
    currentLocationMenuItem || firstAccessibleItem,
  );
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const [isExpanded, setIsExpanded] = useState(!isSmallScreen);
  const menuRef = useRef<HTMLDivElement>(null);

  useClickAway(menuRef, () => {
    if (isSmallScreen && isExpanded) {
      setIsExpanded(false);
    }
  });

  const toggleCollapse = useCallback(
    (id: MenuCategoryId) => {
      set({
        settings_category_collapsed: (draft) => {
          for (const key in settingsCollapsedState) {
            if (!menuCategoryIds.includes(key as MenuCategoryId)) {
              // Cleanup: Remove any keys that are not in the menu
              draft[key] = null;
            }
          }
          draft[id] = !settingsCollapsedState[id];
          return draft;
        },
      });
    },
    [set, settingsCollapsedState],
  );

  return (
    <div
      ref={menuRef}
      className={classnames(styles.menuWrapper, {
        [styles.menuWrapperExpanded]: isExpanded,
      })}
    >
      <ButtonList className={styles.buttonList}>
        {isSmallScreen && (
          <ButtonList.Item
            icon={isExpanded ? 'angle-double-left' : 'angle-double-right'}
            onClick={() => setIsExpanded(!isExpanded)}
            buttonClassName={classnames(
              styles.menuItem,
              styles.menuItemControl,
            )}
            data-testid={`menu-expander`}
          >
            <Text.Paragraph subdued>
              {isExpanded ? 'Collapse' : <>&nbsp;</>}
            </Text.Paragraph>
          </ButtonList.Item>
        )}

        {menu.map((category) => {
          const isCollapsed =
            (settingsCollapsedState[category.id || ''] && !isSmallScreen) ??
            false;
          return (
            <Category
              key={category.id}
              category={category}
              activeItem={activeItem}
              isCollapsed={isCollapsed}
              toggleCollapse={toggleCollapse}
              setActiveItem={setActiveItem}
            />
          );
        })}
      </ButtonList>
    </div>
  );
}

const Category = ({
  category: { id, title = '', items },
  activeItem,
  isCollapsed,
  toggleCollapse: toggleCollapseCb,
  setActiveItem,
}) => {
  const isSmallScreen = useBreakpointValue({ base: true, md: false });
  const toggleCollapse = useCallback(() => {
    toggleCollapseCb(id);
  }, [id, toggleCollapseCb]);

  const showHeader = title && !isSmallScreen;

  return (
    <Fragment>
      <Collapsible
        isOpen={!isCollapsed}
        header={
          showHeader && (
            <li>
              <button
                onClick={toggleCollapse}
                className={classnames(styles.menuTitle, styles.menuItemControl)}
              >
                {title}

                <div
                  className={classnames({
                    [styles.categoryCollapseIcon]: true,
                    [styles.categoryCollapseIconCollapsed]: isCollapsed,
                  })}
                >
                  <Icon name="angle-down" />
                </div>
              </button>
            </li>
          )
        }
      >
        {items.map((item, i) => (
          <MenuItem
            key={i}
            item={item}
            activeItem={activeItem}
            setActiveItem={setActiveItem}
          />
        ))}
      </Collapsible>
    </Fragment>
  );
};

const MenuItem = ({ item, activeItem, setActiveItem }) => {
  const { route, segmentId } = item;
  const isActive = activeItem.segmentId === segmentId;

  return (
    <Item
      key={route}
      item={item}
      isItemActive={isActive}
      setActiveItem={setActiveItem}
    />
  );
};
